// Timezone detection and formatting utilities.
//
// Booking times are wall-clock times in the studio's zone (Europe/Brussels).
// Everything here works on real instants: a studio wall-clock time is first
// turned into an instant via the studio zone's UTC offset at that moment, and
// only then shown in the visitor's zone. Two zones are "the same" when their
// offsets match at the relevant moment, not when their IANA names match:
// Europe/Amsterdam, Europe/Paris and Europe/Brussels share the same clock.

export const STUDIO_TIMEZONE = 'Europe/Brussels'

/**
 * Get the user's detected timezone
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Get timezone display name
 */
export const getTimezoneDisplayName = (timezone?: string): string => {
  const tz = timezone || getUserTimezone()

  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('nl-NL', {
      timeZone: tz,
      timeZoneName: 'long'
    })

    const parts = formatter.formatToParts(now)
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value

    return timeZoneName || tz
  } catch {
    return tz
  }
}

/**
 * Format date with timezone
 */
export const formatDateWithTimezone = (
  date: Date | string,
  timezone?: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const tz = timezone || getUserTimezone()
  const dateObj = typeof date === 'string' ? new Date(date) : date

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: tz,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  }

  return new Intl.DateTimeFormat('nl-NL', defaultOptions).format(dateObj)
}

interface WallClock {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

/** The wall-clock time of an instant in a zone. */
const wallClockIn = (instant: Date, timezone: string): WallClock => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(instant)
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(p => p.type === type)?.value)
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second')
  }
}

const wallClockAsUtc = (w: WallClock): number =>
  Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second)

/**
 * UTC offset of a zone at an instant, in minutes (Brussels in summer: 120).
 */
export const getTimezoneOffset = (timezone: string, instant: Date = new Date()): number => {
  return Math.round((wallClockAsUtc(wallClockIn(instant, timezone)) - instant.getTime()) / 60000) || 0
}

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * The instant of a wall-clock time ("2026-10-23", "07:00" or "07:00:00") in
 * a zone, or null when the input is not a valid date/time.
 */
export const zonedTimeToInstant = (date: string, time: string, timezone: string): Date | null => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  const timeMatch = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(time.trim())
  if (!dateMatch || !timeMatch) return null

  const wall: WallClock = {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
    second: Number(timeMatch[3] ?? 0)
  }
  const asUtc = wallClockAsUtc(wall)
  if (Number.isNaN(asUtc) || wall.hour > 23 || wall.minute > 59 || wall.second > 59) return null

  try {
    // First guess with the offset at the UTC reading, then correct once with
    // the offset at the guessed instant (matters around a DST switch).
    const guess = new Date(asUtc - getTimezoneOffset(timezone, new Date(asUtc)) * 60000)
    return new Date(asUtc - getTimezoneOffset(timezone, guess) * 60000)
  } catch {
    return null
  }
}

/**
 * Convert booking time (studio wall-clock) to the user's timezone.
 * Returns date as YYYY-MM-DD and time as HH:MM in that zone.
 */
export const convertBookingTimeToUserTimezone = (
  bookingDate: string,
  startTime: string,
  studioTimezone: string = STUDIO_TIMEZONE,
  userTimezone: string = getUserTimezone()
): { date: string; time: string; timezone: string } => {
  const fallback = { date: bookingDate, time: startTime.substring(0, 5), timezone: studioTimezone }
  const instant = zonedTimeToInstant(bookingDate, startTime, studioTimezone)
  if (!instant) return fallback

  try {
    const w = wallClockIn(instant, userTimezone)
    return {
      date: `${w.year}-${pad(w.month)}-${pad(w.day)}`,
      time: `${pad(w.hour)}:${pad(w.minute)}`,
      timezone: userTimezone
    }
  } catch {
    return fallback
  }
}

/**
 * Show the timezone warning only when the user's clock really differs from
 * the studio's at the given moment (the booking's start, or now).
 */
export const shouldShowTimezoneWarning = (
  studioTimezone: string = STUDIO_TIMEZONE,
  at: Date = new Date(),
  userTimezone: string = getUserTimezone()
): boolean => {
  if (!userTimezone || userTimezone === studioTimezone) return false
  try {
    return getTimezoneOffset(userTimezone, at) !== getTimezoneOffset(studioTimezone, at)
  } catch {
    return false
  }
}

/**
 * Countdown to a booking (studio wall-clock time), independent of the
 * visitor's timezone.
 */
export const getTimeUntilBooking = (
  bookingDate: string,
  startTime: string,
  studioTimezone: string = STUDIO_TIMEZONE
): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
  isExpired: boolean;
} => {
  const now = new Date()

  // Extract just the date part from bookingDate (handle both YYYY-MM-DD and ISO formats)
  const dateOnly = bookingDate.includes('T') ? bookingDate.split('T')[0] : bookingDate

  const bookingDateTime = zonedTimeToInstant(dateOnly, startTime, studioTimezone)

  if (!bookingDateTime) {
    console.error('Invalid booking datetime:', { bookingDate, dateOnly, startTime })
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0, isExpired: true }
  }

  const total = bookingDateTime.getTime() - now.getTime()
  const isExpired = total <= 0

  if (isExpired) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total, isExpired }
  }

  const days = Math.floor(total / (1000 * 60 * 60 * 24))
  const hours = Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((total % (1000 * 60)) / 1000)

  return { days, hours, minutes, seconds, total, isExpired }
}
